import React, { useState, useEffect, useRef } from 'react';
import LocationSearchBar from '../components/LocationSearchBar';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaSpinner } from 'react-icons/fa';
import '../styles/Home.css';
import { fetchRestaurants, fetchGroceryStores } from '../api/apiService';
import { API_BASE_URL } from '../api/apiService';
import { restaurantCategories, groceryCategories } from '../data/categories';
import RiderHiringAnnouncement from '../components/RiderHiringAnnouncement';

const AnimatedSection = ({ children }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="animated-section"
    >
        {children}
    </motion.div>
);

// Helper function to pad array to 9 items
const padArray = (arr, length) => {
    const padded = [...arr];
    while (padded.length < length) {
        padded.push(null); // Add nulls for empty slots
    }
    return padded;
};

const Home = () => {
    const [restaurants, setRestaurants] = useState([]);
    const [filteredRestaurants, setFilteredRestaurants] = useState([]);
    const [groceryStores, setGroceryStores] = useState([]);
    const [filteredGroceryStores, setFilteredGroceryStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Refs
    const restScrollRef = useRef(null);
    const grocScrollRef = useRef(null);

    // Arrow Visibility States
    const [showRestLeft, setShowRestLeft] = useState(false);
    const [showRestRight, setShowRestRight] = useState(true);
    const [showGrocLeft, setShowGrocLeft] = useState(false);
    const [showGrocRight, setShowGrocRight] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const [rRes, gRes] = await Promise.all([
                    fetchRestaurants(),
                    fetchGroceryStores()
                ]);
                setRestaurants(rRes.data.restaurants || []);
                setFilteredRestaurants(rRes.data.restaurants || []);
                setGroceryStores(gRes.data.groceryStores || []);
                setFilteredGroceryStores(gRes.data.groceryStores || []);
            } catch (err) {
                console.error("Failed to load home data", err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    // --- Arrow Logic ---
    const checkScroll = (ref, setLeft, setRight) => {
        if (!ref.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = ref.current;
        // Show left if scrolled > 0
        setLeft(scrollLeft > 5);
        // Show right if we haven't reached the end
        setRight(scrollLeft + clientWidth < scrollWidth - 5);
    };

    const handleScroll = (ref, direction) => {
        if (ref.current) {
            const amount = 200;
            ref.current.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
        }
    };

    // Attach scroll event listeners to update arrows while scrolling
    useEffect(() => {
        const rRef = restScrollRef.current;
        const gRef = grocScrollRef.current;

        const onRestScroll = () => checkScroll(restScrollRef, setShowRestLeft, setShowRestRight);
        const onGrocScroll = () => checkScroll(grocScrollRef, setShowGrocLeft, setShowGrocRight);

        if (rRef) {
            rRef.addEventListener('scroll', onRestScroll);
            checkScroll(restScrollRef, setShowRestLeft, setShowRestRight); // Initial check
        }
        if (gRef) {
            gRef.addEventListener('scroll', onGrocScroll);
            checkScroll(grocScrollRef, setShowGrocLeft, setShowGrocRight); // Initial check
        }

        return () => {
            if (rRef) rRef.removeEventListener('scroll', onRestScroll);
            if (gRef) gRef.removeEventListener('scroll', onGrocScroll);
        };
    }, [restaurants, groceryStores]); // Re-run when data loads

    const handleSearch = (query) => {
        const term = query.trim().toLowerCase();
        if (!term) {
            setFilteredRestaurants(restaurants);
            setFilteredGroceryStores(groceryStores);
            return;
        }
        const rMatches = restaurants.filter(r => 
            r.name?.toLowerCase().includes(term) || r.cuisine?.toLowerCase().includes(term)
        );
        const gMatches = groceryStores.filter(g => 
            g.name?.toLowerCase().includes(term)
        );
        navigate('/search-results', { 
            state: { restaurantResults: rMatches, groceryResults: gMatches, query } 
        });
    };

    const getImageUrl = (path) => {
        if (!path) return 'https://via.placeholder.com/300?text=No+Image';
        return path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
    };

    if (loading) return (
        <div className="home-loader">
            <FaSpinner className="spinner" /> 
            <p>Loading...</p>
        </div>
    );

    return (
        <div className="home-wrapper">
            <div className="home-container">
                <RiderHiringAnnouncement />
                <LocationSearchBar onSearch={handleSearch} />

                <div className="home-split-layout">
                    
                    {/* --- LEFT COLUMN: RESTAURANTS --- */}
                    <div className="home-section">
                        <AnimatedSection>
                            <h2 className="section-title">Restaurants</h2>
                            
                            {/* Categories with Arrows */}
                            <div className="category-scroll-wrapper">
                                {showRestLeft && (
                                    <button className="scroll-btn left" onClick={() => handleScroll(restScrollRef, 'left')}>
                                        <FaChevronLeft />
                                    </button>
                                )}
                                <div className="category-track" ref={restScrollRef}>
                                    {restaurantCategories.map((cat, i) => (
                                        <div key={i} className="category-card" onClick={() => navigate('/search-results', { state: { query: cat.alt } })}>
                                            <div className="cat-img-box">
                                                <img src={cat.src} alt={cat.alt} />
                                            </div>
                                            <span>{cat.alt}</span>
                                        </div>
                                    ))}
                                </div>
                                {showRestRight && (
                                    <button className="scroll-btn right" onClick={() => handleScroll(restScrollRef, 'right')}>
                                        <FaChevronRight />
                                    </button>
                                )}
                            </div>

                            {/* 3x3 Grid */}
                            <div className="vendor-glass-panel">
                                <div className="vendor-grid">
                                    {padArray(filteredRestaurants.slice(0, 9), 9).map((r, index) => (
                                        r ? (
                                            // RENDER REAL CARD
                                           <Link to={`/restaurants/${r.slug || r._id}`} key={r._id} className="vendor-card">
                                                <div className="vendor-img-container">
                                                    <img src={getImageUrl(r.image)} alt={r.name} />
                                                </div>
                                                <div className="vendor-info">
                                                    <h3>{r.name}</h3>
                                                </div>
                                            </Link>
                                        ) : (
                                            // RENDER EMPTY PLACEHOLDER CARD
                                            <div key={`empty-${index}`} className="vendor-card" style={{opacity: 0.3, border: '1px dashed #ccc'}}>
                                                <div className="vendor-img-container" style={{background: 'transparent'}}></div>
                                                <div className="vendor-info"><h3></h3></div>
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>
                        </AnimatedSection>
                    </div>

                    {/* --- RIGHT COLUMN: GROCERIES --- */}
                    <div className="home-section">
                        <AnimatedSection>
                            <h2 className="section-title">Groceries</h2>
                            
                            {/* Categories with Arrows */}
                            <div className="category-scroll-wrapper">
                                {showGrocLeft && (
                                    <button className="scroll-btn left" onClick={() => handleScroll(grocScrollRef, 'left')}>
                                        <FaChevronLeft />
                                    </button>
                                )}
                                <div className="category-track" ref={grocScrollRef}>
                                    {groceryCategories.map((cat, i) => (
                                        <div key={i} className="category-card" onClick={() => navigate('/search-results', { state: { query: cat.alt } })}>
                                            <div className="cat-img-box">
                                                <img src={cat.src} alt={cat.alt} />
                                            </div>
                                            <span>{cat.alt}</span>
                                        </div>
                                    ))}
                                </div>
                                {showGrocRight && (
                                    <button className="scroll-btn right" onClick={() => handleScroll(grocScrollRef, 'right')}>
                                        <FaChevronRight />
                                    </button>
                                )}
                            </div>

                            {/* 3x3 Grid */}
                            <div className="vendor-glass-panel">
                                <div className="vendor-grid">
                                    {/* ✅ FIX: Apply padArray here too */}
                                    {padArray(filteredGroceryStores.slice(0, 9), 9).map((g, index) => (
                                        g ? (
                                            // RENDER REAL CARD
                                            <Link to={`/grocery-stores/${g.slug || g._id}`} key={g._id} className="vendor-card">
                                                <div className="vendor-img-container">
                                                    <img src={getImageUrl(g.image)} alt={g.name} />
                                                </div>
                                                <div className="vendor-info">
                                                    <h3>{g.name}</h3>
                                                </div>
                                            </Link>
                                        ) : (
                                            // RENDER EMPTY PLACEHOLDER CARD
                                            <div key={`empty-groc-${index}`} className="vendor-card" style={{opacity: 0.3, border: '1px dashed #ccc'}}>
                                                <div className="vendor-img-container" style={{background: 'transparent'}}></div>
                                                <div className="vendor-info"><h3></h3></div>
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>
                        </AnimatedSection>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Home;